// src/pages/products/EditProductPage/hooks/useFormData.js
import { useState, useEffect, useCallback } from 'react';
import { getProductAttributes } from '../../../../services/api/attributes';
import { getShops } from '../../../../services/api/shops';
import { getWarehouses } from '../../../../services/api/warehouses';
import { getProduct, checkProductSku } from '../../../../services/api/products';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { getImageUrl } from '../../../../utils/imageUtils';
import { validateBundle, serializeBundleItems, bundleStateFromProduct } from '../../../../components/product/BundleEditor/bundleUtils';
import { hasPrice } from '../../../../utils/priceUtils';

const useFormData = (productId) => {
  const { t } = useLocalization();
  
  const [formData, setFormData] = useState({
    name: '',
    printableName: '',
    description: '',
    productType: 'simple',
    visibility: 'public',
    buyingPrice: '',
    sku: '',
    barcode: '',
    weight: '',
    brandId: '',
    categoryId: null,
    tagIds: [],
    attributeIds: [],
    attributeValueIds: [],
    shopData: [],
    openingStock: [],
    variations: [],
    bundleItems: [],
    bundleShopIds: [],
    images: [null, null, null, null, null], // 5 slots total (main + 4 gallery)
    mainImageIndex: 0,
    dimensions: { length: '', width: '', height: '' },
    imagesToDelete: [],
    newMainImageFromGallery: null
  });
  
  const [originalProduct, setOriginalProduct] = useState(null);
  const [attributes, setAttributes] = useState([]);
  const [shops, setShops] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isLoadingProduct, setIsLoadingProduct] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');

  // SKU validation state
  const [skuValidation, setSkuValidation] = useState({
    mainSku: { isValidating: false, isValid: true },
    variations: {}
  });

  // Variations state
  const [variationCombinations, setVariationCombinations] = useState([]);
  const [expandedVariations, setExpandedVariations] = useState(new Set());
  const [selectedVariations, setSelectedVariations] = useState(new Set());
  
  // Bulk actions state
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkShopAssignments, setBulkShopAssignments] = useState({});
  const [bulkPrices, setBulkPrices] = useState({});
  const [bulkCostPrice, setBulkCostPrice] = useState('');
  const [bulkWeight, setBulkWeight] = useState('');

  // Debounced SKU validation function
  const validateSku = useCallback(
    debounce(async (sku, type, variationIndex = null) => {
      if (!sku.trim()) {
        // Clear validation state if SKU is empty
        if (type === 'main') {
          setSkuValidation(prev => ({
            ...prev,
            mainSku: { isValidating: false, isValid: true }
          }));
        } else if (type === 'variation' && variationIndex !== null) {
          setSkuValidation(prev => ({
            ...prev,
            variations: {
              ...prev.variations,
              [variationIndex]: { isValidating: false, isValid: true }
            }
          }));
        }
        return;
      }

      try {
        // Set validating state
        if (type === 'main') {
          setSkuValidation(prev => ({
            ...prev,
            mainSku: { isValidating: true, isValid: true }
          }));
        } else if (type === 'variation' && variationIndex !== null) {
          setSkuValidation(prev => ({
            ...prev,
            variations: {
              ...prev.variations,
              [variationIndex]: { isValidating: true, isValid: true }
            }
          }));
        }

        const response = await checkProductSku(sku, productId);
        const isValid = response.valid;

        // Update validation state
        if (type === 'main') {
          setSkuValidation(prev => ({
            ...prev,
            mainSku: { isValidating: false, isValid }
          }));

          // Update errors
          if (!isValid) {
            setErrors(prev => ({
              ...prev,
              sku: 'This SKU is already in use'
            }));
          } else {
            setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.sku;
              return newErrors;
            });
          }
        } else if (type === 'variation' && variationIndex !== null) {
          setSkuValidation(prev => ({
            ...prev,
            variations: {
              ...prev.variations,
              [variationIndex]: { isValidating: false, isValid }
            }
          }));

          // Update variation errors
          if (!isValid) {
            setErrors(prev => ({
              ...prev,
              variations: {
                ...prev.variations,
                [variationIndex]: {
                  ...prev.variations?.[variationIndex],
                  sku: 'This SKU is already in use'
                }
              }
            }));
          } else {
            setErrors(prev => {
              const newErrors = { ...prev };
              if (newErrors.variations?.[variationIndex]?.sku) {
                delete newErrors.variations[variationIndex].sku;
                if (Object.keys(newErrors.variations[variationIndex]).length === 0) {
                  delete newErrors.variations[variationIndex];
                }
                if (Object.keys(newErrors.variations).length === 0) {
                  delete newErrors.variations;
                }
              }
              return newErrors;
            });
          }
        }
      } catch (error) {
        console.error('SKU validation error:', error);
        // Reset validating state on error
        if (type === 'main') {
          setSkuValidation(prev => ({
            ...prev,
            mainSku: { isValidating: false, isValid: true }
          }));
        } else if (type === 'variation' && variationIndex !== null) {
          setSkuValidation(prev => ({
            ...prev,
            variations: {
              ...prev.variations,
              [variationIndex]: { isValidating: false, isValid: true }
            }
          }));
        }
      }
    }, 500),
    [productId]
  );

  const hasBundleItemsChanged = useCallback(() => {
    if (formData.productType !== 'bundle') return false;
    if (!originalProduct) return true;
    const original = bundleStateFromProduct(originalProduct.bundleItems);
    return JSON.stringify(serializeBundleItems(formData.bundleItems, formData.bundleShopIds))
      !== JSON.stringify(serializeBundleItems(original.bundleItems, original.bundleShopIds));
  }, [formData.productType, formData.bundleItems, formData.bundleShopIds, originalProduct]);

  // Check if a field has changed from its original value
  const hasFieldChanged = useCallback((fieldName) => {
    if (!originalProduct) return true; // If no original data, treat as changed
    
    switch (fieldName) {
      case 'name':
        return formData.name !== (originalProduct.name || '');
      case 'printableName':
        return formData.printableName !== (originalProduct.printableName || '');
      case 'description':
        return formData.description !== (originalProduct.description || '');
      case 'type':
        return formData.productType !== (originalProduct.type || 'simple');
      case 'visibility':
        return formData.visibility !== (originalProduct.visibility || 'public');
      case 'costPrice':
        return formData.buyingPrice !== (originalProduct.costPrice?.toString() || '');
      case 'sku':
        return formData.sku !== (originalProduct.sku || '');
      case 'barcode':
        return formData.barcode !== (originalProduct.barcode || '');
      case 'weight':
        return formData.weight !== (originalProduct.weight?.toString() || '');
      case 'brandId':
        return formData.brandId !== (originalProduct.brandId?.toString() || '');
      case 'categoryId':
        return formData.categoryId !== (originalProduct.productCategories?.[0]?.id || null);
      case 'tagIds':
        const originalTagIds = originalProduct.productTags?.map(tag => tag.id) || [];
        return formData.tagIds.length !== originalTagIds.length || 
               !formData.tagIds.every(id => originalTagIds.includes(id));
      case 'attributeIds':
        const originalAttributeIds = originalProduct.attributeSelections?.map(attr => attr.attributeId) || [];
        return formData.attributeIds.length !== originalAttributeIds.length ||
               !formData.attributeIds.every(attr => originalAttributeIds.includes(attr.attributeId));
      case 'attributeValueIds':
        const originalAttributeValueIds = originalProduct.attributeValueSelections?.map(val => val.attributeValueId) || [];
        return formData.attributeValueIds.length !== originalAttributeValueIds.length ||
               !formData.attributeValueIds.every(id => originalAttributeValueIds.includes(id));
      case 'shopData':
        return hasShopDataChanged();
      case 'variations':
        return hasVariationsChanged();
      case 'bundleItems':
        return hasBundleItemsChanged();
      case 'dimensions':
        const originalDimensions = originalProduct.dimensions || {};
        return formData.dimensions.length !== (originalDimensions.length?.toString() || '') ||
               formData.dimensions.width !== (originalDimensions.width?.toString() || '') ||
               formData.dimensions.height !== (originalDimensions.height?.toString() || '');
      default:
        return true;
    }
  }, [formData, originalProduct]);

  // Build image operations object
  const buildImageOperations = useCallback(() => {
    const operations = {
      deleteImages: [],
      setMainImageFromGallery: null
    };
    
    // Find images marked for deletion
    if (formData.imagesToDelete && formData.imagesToDelete.length > 0) {
      operations.deleteImages = formData.imagesToDelete;
    }
    
    // Check if main image changed to an existing gallery image
    if (formData.newMainImageFromGallery) {
      operations.setMainImageFromGallery = formData.newMainImageFromGallery;
    }
    
    return operations;
  }, [formData.imagesToDelete, formData.newMainImageFromGallery]);

  // Get new main image file
  const getNewMainImage = useCallback(() => {
    const mainImageSlot = formData.images[formData.mainImageIndex];
    if (mainImageSlot && mainImageSlot.isNew && mainImageSlot.file instanceof File) {
      return mainImageSlot.file;
    }
    return null;
  }, [formData.images, formData.mainImageIndex]);

  // Get new gallery image files
  const getNewGalleryImages = useCallback(() => {
    return formData.images
      .filter((img, index) => 
        img && 
        index !== formData.mainImageIndex && 
        img.isNew && 
        img.file instanceof File
      )
      .map(img => img.file);
  }, [formData.images, formData.mainImageIndex]);

  // Check if shop data has changed
  const hasShopDataChanged = useCallback(() => {
    const originalShops = originalProduct?.productShops || [];
    return formData.shopData.some(shop => {
      if (!shop.enabled) return false;
      const originalShop = originalShops.find(s => s.shopId === shop.shopId);
      if (!originalShop) return true;
      const originalOverride = originalShop.overrideDeliveryCharge != null
        ? originalShop.overrideDeliveryCharge.toString()
        : null;
      const currentOverride = shop.overrideDeliveryChargeEnabled && shop.overrideDeliveryCharge != null && shop.overrideDeliveryCharge !== ''
        ? shop.overrideDeliveryCharge
        : null;
      return shop.salesPrice !== (originalShop.price?.toString() || '') ||
            shop.dropshippingPrice !== (originalShop.dropshippingPrice?.toString() || '') ||
            currentOverride !== originalOverride ||
            JSON.stringify(shop.offers || []) !== JSON.stringify(originalShop.offers || []);
    });
  }, [formData.shopData, originalProduct]);

  // Check if variations have changed
  const hasVariationsChanged = useCallback(() => {
    const originalVariations = originalProduct?.variations || [];
    
    if (formData.variations.length !== originalVariations.length) return true;
    
    return formData.variations.some(variation => {
      if (!variation.id) return true; // New variation
      
      const originalVariation = originalVariations.find(v => v.id === variation.id);
      if (!originalVariation) return true;
      
      return variation.sku !== (originalVariation.sku || '') ||
             variation.costPrice !== (originalVariation.costPrice?.toString() || '') ||
             variation.barcode !== (originalVariation.barcode || '') ||
             variation.weight !== (originalVariation.weight?.toString() || '') ||
             variation.deleteImage ||
             variation.variationImage instanceof File ||
             hasVariationShopDataChanged(variation, originalVariation);
    });
  }, [formData.variations, originalProduct]);

  // Check if variation shop data has changed
  const hasVariationShopDataChanged = useCallback((variation, originalVariation) => {
    return variation.shopData.some(shop => {
      if (!shop.enabled) return false;
      const originalShop = originalVariation.variationShops?.find(s => s.shopId === shop.shopId);
      if (!originalShop) return true;
      const originalOverride = originalShop.overrideDeliveryCharge != null
        ? originalShop.overrideDeliveryCharge.toString()
        : null;
      const currentOverride = shop.overrideDeliveryChargeEnabled && shop.overrideDeliveryCharge != null && shop.overrideDeliveryCharge !== ''
        ? shop.overrideDeliveryCharge
        : null;
      return shop.salesPrice !== (originalShop.price?.toString() || '') ||
            shop.dropshippingPrice !== (originalShop.dropshippingPrice?.toString() || '') ||
            currentOverride !== originalOverride ||
            JSON.stringify(shop.offers || []) !== JSON.stringify(originalShop.offers || []);
    });
  }, []);

  // Initialize form data with product data
  const initializeFormData = useCallback((product) => {
    console.log('Initializing form data with product:', product);
    
    // Map product data according to API response structure
    const mappedFormData = {
      name: product.name || '',
      printableName: product.printableName || '',
      description: product.description || '',
      productType: product.type || 'simple',
      visibility: product.visibility || 'public',
      buyingPrice: product.costPrice?.toString() || '',
      sku: product.sku || '',
      barcode: product.barcode || '',
      weight: product.weight?.toString() || '',
      brandId: product.brandId || '',
      
      // Map category from productCategories array
      categoryId: product.productCategories?.[0]?.id || null,
      
      // Map tags from productTags array
      tagIds: product.productTags?.map(tag => tag.id) || [],
      
      // Map attributes from attributeSelections array
      attributeIds: product.attributeSelections?.map(attr => ({
        attributeId: attr.attributeId,
        usedForVariations: attr.usedForVariations || false
      })) || [],
      
      // Map attribute values from attributeValueSelections array
      attributeValueIds: product.attributeValueSelections?.map(val => val.attributeValueId) || [],
      
      // Initialize shop data based on existing shops
      shopData: shops.map(shop => {
        // Find matching shop in productShops array
        const productShop = product.productShops?.find(ps => ps.shopId === shop.id);
        return {
          shopId: shop.id,
          salesPrice: productShop ? productShop.price?.toString() || '' : '',
          dropshippingPrice: productShop ? productShop.dropshippingPrice?.toString() || '' : '',
          enabled: !!productShop,
          overrideDeliveryChargeEnabled: productShop && productShop.overrideDeliveryCharge != null,
          overrideDeliveryCharge: productShop?.overrideDeliveryCharge != null
            ? productShop.overrideDeliveryCharge.toString()
            : null,
          offers: productShop?.offers || [],
        };
      }),
      
      // Initialize opening stock based on existing stocks
      openingStock: warehouses.map(warehouse => {
        const productStock = product.stocks?.find(s => s.warehouseId === warehouse.id);
        return {
          warehouseId: warehouse.id,
          stock: productStock ? productStock.stock?.toString() || '' : ''
        };
      }),
      
      // Map variations if product is variable
      variations: product.variations?.map((variation, index) => {
        console.log('Mapping variation:', variation);
        
        // Extract attribute value IDs from the attributeValues array
        const attributeValueIds = variation.attributeValues?.map(attrVal => attrVal.id) || [];
        
        // Map attribute values for display
        const attributeValues = variation.attributeValues?.map(attrVal => ({
          id: attrVal.id,
          value: attrVal.value,
          attributeId: attrVal.attributeId
        })) || [];
        
        return {
          id: variation.id,
          sku: variation.sku || '',
          costPrice: variation.costPrice?.toString() || '',
          barcode: variation.barcode || '',
          weight: variation.weight?.toString() || '',
          attributeValueIds: attributeValueIds,
          attributeValues: attributeValues,
          variationImage: null, // Handle existing variation images separately

          // Dropshipping fields per variation
          dropshippingEnabled: !!variation.dropshippingEnabled,
          dropshippingPrice: variation.dropshippingPrice?.toString() || '',
          minimumSellingPrice: variation.minimumSellingPrice?.toString() || '',
          maximumSellingPrice: variation.maximumSellingPrice?.toString() || '',

          // Map shop data for this variation
          shopData: shops.map(shop => {
            const variationShop = variation.variationShops?.find(vs => vs.shopId === shop.id);
            return {
              shopId: shop.id,
              salesPrice: variationShop ? variationShop.price?.toString() || '' : '',
              dropshippingPrice: variationShop ? variationShop.dropshippingPrice?.toString() || '' : '',
              enabled: !!variationShop,
              overrideDeliveryChargeEnabled: variationShop && variationShop.overrideDeliveryCharge != null,
              overrideDeliveryCharge: variationShop?.overrideDeliveryCharge != null
                ? variationShop.overrideDeliveryCharge.toString()
                : null,
              offers: variationShop?.offers || [],
            };
          }),

          // Map opening stock for this variation
          openingStock: warehouses.map(warehouse => {
            const variationStock = variation.stocks?.find(s => s.warehouseId === warehouse.id);
            return {
              warehouseId: warehouse.id,
              stock: variationStock ? variationStock.stock?.toString() || '' : ''
            };
          }),

          // Handle existing variation images
          existingImages: variation.images?.map(img => ({
            id: img.id,
            url: getImageUrl(img.path),
            path: img.path,
            isMain: img.isMain
          })) || []
        };
      }) || [],
      
      // Initialize images array with existing product images
      images: [null, null, null, null, null],
      mainImageIndex: 0,
      
      // Map dimensions
      dimensions: {
        length: product.dimensions?.length?.toString() || '',
        width: product.dimensions?.width?.toString() || '',
        height: product.dimensions?.height?.toString() || ''
      },

      // Initialize image tracking
      imagesToDelete: [],
      newMainImageFromGallery: null,

      ...bundleStateFromProduct(product.bundleItems)
    };

    // Handle existing images
    if (product.images && product.images.length > 0) {
      // Sort images by main image first, then by order
      const sortedImages = [...product.images].sort((a, b) => {
        if (a.isMain && !b.isMain) return -1;
        if (!a.isMain && b.isMain) return 1;
        return a.id - b.id;
      });
      
      // Find main image index
      const mainImageIndex = product.images.findIndex(img => img.isMain);
      mappedFormData.mainImageIndex = mainImageIndex >= 0 ? mainImageIndex : 0;
      
      // Populate images array with existing image data
      sortedImages.forEach((img, index) => {
        if (index < 5) { // Only take first 5 images
          mappedFormData.images[index] = {
            isExisting: true,
            url: getImageUrl(img.path), // Use utility function to get full URL
            id: img.id,
            path: img.path,
            isMain: img.isMain
          };
        }
      });
    }

    console.log('Mapped form data:', mappedFormData);
    setFormData(mappedFormData);
    setVariationCombinations(mappedFormData.variations);
  }, [shops, warehouses]);

  // Available tabs based on product type
  const getAvailableTabs = () => {
    const baseTabs = [
      { id: 'basic', label: t('products.basicInfo'), icon: 'Info' },
      { id: 'pricing', label: t('products.pricing'), icon: 'DollarSign' },
      { id: 'attributes', label: t('products.attributes'), icon: 'Tag' },
    ];

    if (formData.productType === 'variable') {
      baseTabs.push({ id: 'variations', label: 'Variations', icon: 'Settings' });
    }

    baseTabs.push({ id: 'images', label: t('products.images'), icon: 'ImageIcon' });
    return baseTabs;
  };

  // Fetch required data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        
        const [
          attributesResponse, 
          shopsResponse,
          warehousesResponse
        ] = await Promise.all([
          getProductAttributes(),
          getShops(),
          getWarehouses()
        ]);
        
        setAttributes(attributesResponse.attributes || []);
        setShops(shopsResponse || []);
        setWarehouses(warehousesResponse || []);
        
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setErrors({ fetch: t('products.failedToLoadData') });
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [t]);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId || shops.length === 0 || warehouses.length === 0) return;
      
      try {
        setIsLoadingProduct(true);
        const response = await getProduct(productId);
        const product = response.product;
        
        console.log('Fetched product:', product);
        setOriginalProduct(product);
        initializeFormData(product);
        
      } catch (error) {
        console.error('Failed to fetch product:', error);
        setErrors({ fetch: t('products.failedToLoadProduct') });
      } finally {
        setIsLoadingProduct(false);
      }
    };

    if (shops.length > 0 && warehouses.length > 0) {
      fetchProduct();
    }
  }, [productId, shops, warehouses, t, initializeFormData]);

  // Update attribute values when attributes change
  useEffect(() => {
    if (attributes.length > 0 && formData.variations.length > 0) {
      setFormData(prev => ({
        ...prev,
        variations: prev.variations.map(variation => {
          const attributeValues = variation.attributeValueIds.map(valueId => {
            for (const attribute of attributes) {
              const value = attribute.values?.find(val => val.id === valueId);
              if (value) return value;
            }
            return null;
          }).filter(Boolean);
          
          return {
            ...variation,
            attributeValues
          };
        })
      }));
    }
  }, [attributes, formData.variations.length]);

  // Generate variations when attributes change - but preserve existing variation data
  useEffect(() => {
    if (formData.productType === 'variable' && formData.attributeIds.length > 0 && attributes.length > 0) {
      // Only regenerate if we don't have existing variations with proper attribute data
      const hasValidVariations = formData.variations.length > 0 && 
        formData.variations.every(v => v.attributeValueIds && v.attributeValueIds.length > 0);
      
      if (!hasValidVariations) {
        generateVariations();
      }
    }
  }, [formData.attributeIds, formData.attributeValueIds, formData.productType, attributes]);

  // Reset activeTab when product type changes
  useEffect(() => {
    if (formData.productType !== 'variable' && activeTab === 'variations') {
      setActiveTab('basic');
    }
    if (formData.productType !== 'bundle' && activeTab === 'bundle') {
      setActiveTab('basic');
    }
    if (formData.productType === 'bundle' && ['pricing', 'attributes', 'dropshipping'].includes(activeTab)) {
      setActiveTab('basic');
    }
  }, [formData.productType, activeTab]);

const generateVariations = () => {
  // Get attributes that are used for variations
  const variationAttributeIds = formData.attributeIds
    .filter(attr => attr.usedForVariations)
    .map(attr => attr.attributeId);

  if (variationAttributeIds.length === 0) {
    setVariationCombinations([]);
    setFormData(prev => ({ ...prev, variations: [] }));
    setExpandedVariations(new Set());
    setSelectedVariations(new Set());
    return;
  }

  const attributeValues = variationAttributeIds.map(attrId => {
    const attribute = attributes.find(attr => attr.id === attrId);
    return attribute ? attribute.values?.filter(val => 
      formData.attributeValueIds.includes(val.id)
    ) || [] : [];
  });

  if (attributeValues.some(values => values.length === 0)) {
    setVariationCombinations([]);
    setFormData(prev => ({ ...prev, variations: [] }));
    setExpandedVariations(new Set());
    setSelectedVariations(new Set());
    return;
  }

  // Generate all combinations
  const combinations = cartesianProduct(attributeValues);
  const existingVariations = formData.variations;
  
  const variations = combinations.map((combo, index) => {
    const attributeValueIds = combo.map(value => value.id);
    
    // Check if this combination already exists
    const existingVariation = existingVariations.find(v => 
      v.attributeValueIds.length === attributeValueIds.length &&
      v.attributeValueIds.every(id => attributeValueIds.includes(id))
    );
    
    if (existingVariation) {
      return {
        ...existingVariation,
        attributeValues: combo
      };
    }
    
    // Create new variation with a temporary ID that's clearly identifiable
    // Use a high number that won't conflict with real server IDs
    // const tempId = Date.now() + index;
    
    return {
      // id: tempId, // This will be filtered out when sending to server
      isNew: true, // Flag to identify new variations
      sku: '',
      costPrice: '',
      barcode: '',
      weight: '',
      attributeValueIds: attributeValueIds,
      attributeValues: combo,
      variationImage: null,
      dropshippingEnabled: false,
      dropshippingPrice: '',
      minimumSellingPrice: '',
      maximumSellingPrice: '',
      shopData: shops.map(shop => ({
        shopId: shop.id,
        salesPrice: '',
        dropshippingPrice: '',
        enabled: false,
        overrideDeliveryChargeEnabled: false,
        overrideDeliveryCharge: null,
        offers: [],
      })),
      openingStock: warehouses.map(warehouse => ({
        warehouseId: warehouse.id,
        stock: ''
      }))
    };
  });

  setVariationCombinations(variations);
  setFormData(prev => ({
    ...prev,
    variations: variations
  }));
  
  // Reset expanded and selected states for new variations only
  const newVariationIds = variations
    .filter(v => !existingVariations.find(ev => ev.id === v.id))
    .map(v => v.id);
  
  setExpandedVariations(prev => {
    const newSet = new Set(prev);
    newVariationIds.forEach(id => newSet.delete(id));
    return newSet;
  });
  
  setSelectedVariations(prev => {
    const newSet = new Set(prev);
    newVariationIds.forEach(id => newSet.delete(id));
    return newSet;
  });
  
  // Reset SKU validation for new variations
  setSkuValidation(prev => ({
    ...prev,
    variations: Object.fromEntries(
      Object.entries(prev.variations).filter(([index]) => 
        !newVariationIds.includes(variations[parseInt(index)]?.id)
      )
    )
  }));
};

  const cartesianProduct = (arrays) => {
    return arrays.reduce((acc, curr) => 
      acc.flatMap(x => curr.map(y => [...x, y])), [[]]
    );
  };

  // Variation management functions
  const toggleVariationExpansion = (variationId) => {
    setExpandedVariations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(variationId)) {
        newSet.delete(variationId);
      } else {
        newSet.add(variationId);
      }
      return newSet;
    });
  };

  const toggleVariationSelection = (variationId) => {
    setSelectedVariations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(variationId)) {
        newSet.delete(variationId);
      } else {
        newSet.add(variationId);
      }
      return newSet;
    });
  };

  const selectAllVariations = () => {
    const allIds = formData.variations.map(v => v.id);
    setSelectedVariations(new Set(allIds));
  };

  const deselectAllVariations = () => {
    setSelectedVariations(new Set());
  };

  const removeSelectedVariations = () => {
    const filteredVariations = formData.variations.filter(v => !selectedVariations.has(v.id));
    setFormData(prev => ({
      ...prev,
      variations: filteredVariations
    }));
    setSelectedVariations(new Set());
    setExpandedVariations(prev => {
      const newSet = new Set(prev);
      selectedVariations.forEach(id => newSet.delete(id));
      return newSet;
    });
  };

  const regenerateVariations = () => {
    generateVariations();
  };

  // Bulk actions functions
  const handleBulkShopAssignment = (shopId, enabled) => {
    setBulkShopAssignments(prev => ({
      ...prev,
      [shopId]: enabled
    }));
  };

  const handleBulkPriceChange = (shopId, price) => {
    setBulkPrices(prev => ({
      ...prev,
      [shopId]: price
    }));
  };

  const applyBulkActions = (successCallback, errorCallback) => {
    if (selectedVariations.size === 0) {
      if (errorCallback) errorCallback('Please select variations to apply bulk actions');
      return;
    }

    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map(variation => {
        if (selectedVariations.has(variation.id)) {
          return {
            ...variation,
            costPrice: bulkCostPrice || variation.costPrice,
            weight: bulkWeight || variation.weight,
            shopData: variation.shopData.map(shop => ({
              ...shop,
              enabled: bulkShopAssignments[shop.shopId] !== undefined ? 
                bulkShopAssignments[shop.shopId] : shop.enabled,
              salesPrice: bulkPrices[shop.shopId] !== undefined ? 
                bulkPrices[shop.shopId] : shop.salesPrice,
              dropshippingPrice: bulkPrices[`${shop.shopId}_dropshipping`] !== undefined ? 
                bulkPrices[`${shop.shopId}_dropshipping`] : shop.dropshippingPrice
            }))
          };
        }
        return variation;
      })
    }));

    // Reset bulk actions
    setBulkCostPrice('');
    setBulkWeight('');
    setBulkShopAssignments({});
    setBulkPrices({});
    setShowBulkActions(false);
    setSelectedVariations(new Set());
    
    if (successCallback) successCallback('Bulk actions applied successfully');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Validate SKU if it's the SKU field
    if (name === 'sku') {
      validateSku(value, 'main');
    }
    
    // Clear error when user starts typing (except for SKU which is handled by validation)
    if (errors[name] && name !== 'sku') {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBundleChange = ({ bundleItems, bundleShopIds }) => {
    setFormData(prev => ({ ...prev, bundleItems, bundleShopIds }));
    if (errors.bundleItems || errors.bundleShops) {
      setErrors(prev => ({ ...prev, bundleItems: '', bundleShops: '' }));
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData(prev => ({
      ...prev,
      description: content
    }));
    
    if (errors.description) {
      setErrors(prev => ({
        ...prev,
        description: ''
      }));
    }
  };

  const handleAttributesChange = (newAttributeIds) => {
    setFormData(prev => ({
      ...prev,
      attributeIds: newAttributeIds
    }));

    if (errors.attributes) {
      setErrors(prev => ({
        ...prev,
        attributes: ''
      }));
    }
  };

  const handleAttributeValuesChange = (newAttributeValueIds) => {
    setFormData(prev => ({
      ...prev,
      attributeValueIds: newAttributeValueIds
    }));
  };

  const handleCategorySelect = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categoryId: categoryId
    }));

    if (errors.categoryId) {
      setErrors(prev => ({
        ...prev,
        categoryId: ''
      }));
    }
  };

  const handleTagsChange = (newTagIds) => {
    setFormData(prev => ({
      ...prev,
      tagIds: newTagIds
    }));
    
    if (errors.tagIds) {
      setErrors(prev => ({
        ...prev,
        tagIds: ''
      }));
    }
  };

  const handleBrandChange = (brandId) => {
    setFormData(prev => ({
      ...prev,
      brandId: brandId || ''
    }));
    
    if (errors.brandId) {
      setErrors(prev => ({
        ...prev,
        brandId: ''
      }));
    }
  };

  const handleShopDataChange = (shopIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      shopData: prev.shopData.map((shop, index) => 
        index === shopIndex ? { ...shop, [field]: value } : shop
      )
    }));

    if (errors.shopData && errors.shopData[shopIndex] && errors.shopData[shopIndex][field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.shopData && newErrors.shopData[shopIndex]) {
          delete newErrors.shopData[shopIndex][field];
          if (Object.keys(newErrors.shopData[shopIndex]).length === 0) {
            delete newErrors.shopData[shopIndex];
          }
        }
        return newErrors;
      });
    }
  };

  const handleOpeningStockChange = (warehouseIndex, value) => {
    setFormData(prev => ({
      ...prev,
      openingStock: prev.openingStock.map((stock, index) => 
        index === warehouseIndex ? { ...stock, stock: value } : stock
      )
    }));

    if (errors.openingStock && errors.openingStock[warehouseIndex]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.openingStock && newErrors.openingStock[warehouseIndex]) {
          delete newErrors.openingStock[warehouseIndex];
        }
        return newErrors;
      });
    }
  };

  const handleVariationChange = (variationIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((variation, index) => 
        index === variationIndex ? { ...variation, [field]: value } : variation
      )
    }));

    // Validate SKU if it's the SKU field
    if (field === 'sku') {
      validateSku(value, 'variation', variationIndex);
    }

    // Clear error when user starts typing (except for SKU which is handled by validation)
    if (errors.variations && errors.variations[variationIndex] && errors.variations[variationIndex][field] && field !== 'sku') {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.variations && newErrors.variations[variationIndex]) {
          delete newErrors.variations[variationIndex][field];
          if (Object.keys(newErrors.variations[variationIndex]).length === 0) {
            delete newErrors.variations[variationIndex];
          }
        }
        return newErrors;
      });
    }
  };

  const handleVariationShopDataChange = (variationIndex, shopIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((variation, vIndex) => 
        vIndex === variationIndex ? {
          ...variation,
          shopData: variation.shopData.map((shop, sIndex) => 
            sIndex === shopIndex ? { ...shop, [field]: value } : shop
          )
        } : variation
      )
    }));

    // Clear errors
    if (errors.variations && errors.variations[variationIndex]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.variations[variationIndex]?.shopDataDetails?.[shopIndex]?.[field]) {
          delete newErrors.variations[variationIndex].shopDataDetails[shopIndex][field];
        }
        return newErrors;
      });
    }
  };

  const handleVariationStockChange = (variationIndex, warehouseIndex, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((variation, vIndex) => 
        vIndex === variationIndex ? {
          ...variation,
          openingStock: variation.openingStock.map((stock, sIndex) => 
            sIndex === warehouseIndex ? { ...stock, stock: value } : stock
          )
        } : variation
      )
    }));

    // Clear errors
    if (errors.variations && errors.variations[variationIndex]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.variations[variationIndex]?.openingStock?.[warehouseIndex]) {
          delete newErrors.variations[variationIndex].openingStock[warehouseIndex];
        }
        return newErrors;
      });
    }
  };

  const handleVariationImageChange = (variationIndex, file) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((variation, index) => 
        index === variationIndex ? { ...variation, variationImage: file } : variation
      )
    }));
  };

  const handleImageUpload = (index, file) => {
    if (file) {
      const newImages = [...formData.images];
      newImages[index] = {
        isNew: true,
        file: file,
        url: URL.createObjectURL(file)
      };
      setFormData(prev => ({
        ...prev,
        images: newImages
      }));
    }
  };

  const handleImageRemove = (index) => {
    const imageToRemove = formData.images[index];
    
    // If it's an existing image, mark it for deletion
    if (imageToRemove && imageToRemove.isExisting && imageToRemove.id) {
      setFormData(prev => ({
        ...prev,
        imagesToDelete: [...prev.imagesToDelete, imageToRemove.id]
      }));
    }
    
    const newImages = [...formData.images];
    newImages[index] = null;
    
    // Update main image index if necessary
    let newMainIndex = formData.mainImageIndex;
    if (index === formData.mainImageIndex) {
      newMainIndex = newImages.findIndex((img, i) => img !== null && i !== index);
      if (newMainIndex === -1) newMainIndex = 0;
    }
    
    setFormData(prev => ({
      ...prev,
      images: newImages,
      mainImageIndex: newMainIndex
    }));
  };

  const handleSetMainImage = (index) => {
    const imageToPromote = formData.images[index];
    
    if (imageToPromote && imageToPromote.isExisting && imageToPromote.id) {
      setFormData(prev => ({
        ...prev,
        newMainImageFromGallery: imageToPromote.id
      }));
    }
    
    setFormData(prev => ({
      ...prev,
      mainImageIndex: index
    }));
  };

  const handleVariationDropshippingChange = (variationIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((variation, idx) =>
        idx === variationIndex ? { ...variation, [field]: value } : variation
      )
    }));
  };

  const applyBulkDropshipping = (bulkData) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map(variation => ({
        ...variation,
        dropshippingEnabled: bulkData.dropshippingEnabled,
        ...(bulkData.dropshippingPrice !== '' && { dropshippingPrice: bulkData.dropshippingPrice }),
        ...(bulkData.minimumSellingPrice !== '' && { minimumSellingPrice: bulkData.minimumSellingPrice }),
        ...(bulkData.maximumSellingPrice !== '' && { maximumSellingPrice: bulkData.maximumSellingPrice }),
      }))
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = t('products.nameRequired');
    }

    if (!formData.productType) {
      newErrors.productType = t('products.productTypeRequired');
    }

    if (!formData.visibility) {
      newErrors.visibility = t('products.visibilityRequired');
    }

    // Category validation (now required)
    if (!formData.categoryId) {
      newErrors.categoryId = t('products.categoryRequired') || 'Please select a category';
    }

    // SKU validation - check if any SKU is currently being validated or invalid
    if (formData.sku.trim() && (!skuValidation.mainSku.isValid || skuValidation.mainSku.isValidating)) {
      if (skuValidation.mainSku.isValidating) {
        newErrors.sku = 'SKU validation in progress...';
      } else if (!skuValidation.mainSku.isValid) {
        newErrors.sku = 'This SKU is already in use';
      }
    }

    if (formData.productType === 'bundle') {
      Object.assign(newErrors, validateBundle(formData.bundleItems, formData.bundleShopIds, t));
    } else if (formData.productType === 'variable') {
      const variationAttributes = formData.attributeIds.filter(attr => attr.usedForVariations);
      if (variationAttributes.length === 0) {
        newErrors.attributes = t('products.attributesRequiredForVariable');
      }

      // Check if at least one variation remains
      if (formData.variations.length === 0) {
        newErrors.variations = 'At least one variation is required';
      }

      // Validate each variation
      const variationErrors = [];
      formData.variations.forEach((variation, index) => {
        const varErrors = {};

        // SKU validation for variations
        if (variation.sku.trim()) {
          const variationSkuValidation = skuValidation.variations[index];
          if (variationSkuValidation && (!variationSkuValidation.isValid || variationSkuValidation.isValidating)) {
            if (variationSkuValidation.isValidating) {
              varErrors.sku = 'SKU validation in progress...';
            } else if (!variationSkuValidation.isValid) {
              varErrors.sku = 'This SKU is already in use';
            }
          }
        }

        // Check if any shop has sales price
        const hasValidShopData = variation.shopData.some(shop => shop.enabled && hasPrice(shop.salesPrice));
        if (!hasValidShopData) {
          varErrors.shopData = 'At least one shop must be enabled with a sales price';
        }

        // Validate individual shop data
        const shopDataErrors = [];
        variation.shopData.forEach((shopData, shopIndex) => {
          if (shopData.enabled && !hasPrice(shopData.salesPrice)) {
            shopDataErrors[shopIndex] = { salesPrice: 'Sales price is required for enabled shops' };
          }
        });
        if (shopDataErrors.length > 0) {
          varErrors.shopDataDetails = shopDataErrors;
        }

        // Validate dropshipping fields when enabled
        if (variation.dropshippingEnabled) {
          if (!variation.dropshippingPrice) {
            varErrors.dropshippingPrice = t('dropshipping.dropshippingPriceRequired') || 'Dropshipping price is required';
          }
          if (!variation.minimumSellingPrice) {
            varErrors.minimumSellingPrice = t('dropshipping.minPriceRequired') || 'Minimum selling price is required';
          }
          if (!variation.maximumSellingPrice) {
            varErrors.maximumSellingPrice = t('dropshipping.maxPriceRequired') || 'Maximum selling price is required';
          }
        }

        if (Object.keys(varErrors).length > 0) {
          variationErrors[index] = varErrors;
        }
      });

      if (variationErrors.length > 0) {
        newErrors.variations = variationErrors;
      }
    } else {
      // For simple products, validate shop data
      const shopDataErrors = [];
      formData.shopData.forEach((shop, index) => {
        if (shop.enabled) {
          const shopErrors = {};
          if (!hasPrice(shop.salesPrice)) {
            shopErrors.salesPrice = t('products.salesPriceRequired');
          }
          if (shop.type === 'dropshipper' && (!shop.dropshippingPrice || shop.dropshippingPrice <= 0)) {
            shopErrors.dropshippingPrice = t('products.dropshippingPriceRequired');
          }
          if (Object.keys(shopErrors).length > 0) {
            shopDataErrors[index] = shopErrors;
          }
        }
      });

      if (shopDataErrors.length > 0) {
        newErrors.shopData = shopDataErrors;
      }
    }

    // Image validation
    const validImages = formData.images.filter(img => img !== null);
    if (validImages.length > 0) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      const invalidImages = validImages.some(img => {
        // Skip validation for existing images
        if (img.isExisting) return false;
        return !allowedTypes.includes(img.file?.type) || img.file?.size > 5 * 1024 * 1024;
      });
      if (invalidImages) {
        newErrors.images = t('products.invalidGalleryImages');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form has changes compared to original product
  const hasChanges = () => {
    if (!originalProduct) return false;
    
    // Compare basic fields
    const basicFieldsChanged = (
      formData.name !== (originalProduct.name || '') ||
      formData.description !== (originalProduct.description || '') ||
      formData.productType !== (originalProduct.type || 'simple') ||
      formData.visibility !== (originalProduct.visibility || 'public') ||
      formData.buyingPrice !== (originalProduct.costPrice?.toString() || '') ||
      formData.sku !== (originalProduct.sku || '') ||
      formData.barcode !== (originalProduct.barcode || '') ||
      formData.weight !== (originalProduct.weight?.toString() || '') ||
      formData.brandId !== (originalProduct.brandId?.toString() || '') ||
      formData.categoryId !== (originalProduct.productCategories?.[0]?.id || null)
    );

    if (basicFieldsChanged || hasBundleItemsChanged()) return true;

    // Compare dimensions
    const originalDimensions = originalProduct.dimensions || {};
    const dimensionsChanged = (
      formData.dimensions.length !== (originalDimensions.length?.toString() || '') ||
      formData.dimensions.width !== (originalDimensions.width?.toString() || '') ||
      formData.dimensions.height !== (originalDimensions.height?.toString() || '')
    );

    if (dimensionsChanged) return true;

    // Compare tags
    const originalTagIds = originalProduct.productTags?.map(tag => tag.id) || [];
    const tagsChanged = (
      formData.tagIds.length !== originalTagIds.length ||
      !formData.tagIds.every(id => originalTagIds.includes(id))
    );

    if (tagsChanged) return true;

    // Compare attributes
    const originalAttributeIds = originalProduct.attributeSelections?.map(attr => attr.attributeId) || [];
    const attributesChanged = (
      formData.attributeIds.length !== originalAttributeIds.length ||
      !formData.attributeIds.every(attr => originalAttributeIds.includes(attr.attributeId))
    );

    if (attributesChanged) return true;

    // Compare attribute values
    const originalAttributeValueIds = originalProduct.attributeValueSelections?.map(val => val.attributeValueId) || [];
    const attributeValuesChanged = (
      formData.attributeValueIds.length !== originalAttributeValueIds.length ||
      !formData.attributeValueIds.every(id => originalAttributeValueIds.includes(id))
    );

    if (attributeValuesChanged) return true;

    // Compare shop data
    const originalShops = originalProduct.productShops || [];
    const shopDataChanged = formData.shopData.some(shop => {
      if (!shop.enabled) return false;
      const originalShop = originalShops.find(s => s.shopId === shop.shopId);
      if (!originalShop) return true;
      return (
        shop.salesPrice !== (originalShop.price?.toString() || '') ||
        shop.dropshippingPrice !== (originalShop.dropshippingPrice?.toString() || '')
      );
    });

    if (shopDataChanged) return true;

    // Compare variations for variable products
    if (formData.productType === 'variable') {
      const originalVariations = originalProduct.variations || [];
      
      if (formData.variations.length !== originalVariations.length) return true;
      
      const variationsChanged = formData.variations.some((variation, index) => {
        const originalVariation = originalVariations.find(v => v.id === variation.id);
        if (!originalVariation) return true;
        
        return (
          variation.sku !== (originalVariation.sku || '') ||
          variation.costPrice !== (originalVariation.costPrice?.toString() || '') ||
          variation.barcode !== (originalVariation.barcode || '') ||
          variation.weight !== (originalVariation.weight?.toString() || '') ||
          variation.shopData.some(shop => {
            if (!shop.enabled) return false;
            const originalVariationShop = originalVariation.variationShops?.find(s => s.shopId === shop.shopId);
            if (!originalVariationShop) return true;
            return (
              shop.salesPrice !== (originalVariationShop.price?.toString() || '') ||
              shop.dropshippingPrice !== (originalVariationShop.dropshippingPrice?.toString() || '')
            );
          })
        );
      });

      if (variationsChanged) return true;
    }

    // Check if any new images were added
    const hasNewImages = formData.images.some(img => img && img.isNew);
    if (hasNewImages) return true;

    // Check if images were deleted
    if (formData.imagesToDelete.length > 0) return true;

    // Check if main image index changed
    const originalMainImageIndex = originalProduct.images?.findIndex(img => img.isMain) || 0;
    if (formData.mainImageIndex !== originalMainImageIndex) return true;

    return false;
  };

  return {
    // State
    formData,
    originalProduct,
    attributes,
    shops,
    warehouses,
    errors,
    isLoadingData,
    isLoadingProduct,
    activeTab,
    variationCombinations,
    expandedVariations,
    selectedVariations,
    showBulkActions,
    bulkShopAssignments,
    bulkPrices,
    bulkCostPrice,
    bulkWeight,
    skuValidation,

    // Setters
    setFormData,
    setErrors,
    setActiveTab,
    setShowBulkActions,
    setBulkCostPrice,
    setBulkWeight,

    // Utility functions
    getAvailableTabs,
    validateForm,
    hasChanges,
    hasFieldChanged,
    buildImageOperations,
    getNewMainImage,
    getNewGalleryImages,
    hasShopDataChanged,
    hasVariationsChanged,
    hasVariationShopDataChanged,

    // Handler functions
    handleInputChange,
    handleDescriptionChange,
    handleBundleChange,
    handleAttributesChange,
    handleAttributeValuesChange,
    handleCategorySelect,
    handleTagsChange,
    handleBrandChange,
    handleShopDataChange,
    handleOpeningStockChange,
    handleVariationChange,
    handleVariationShopDataChange,
    handleVariationStockChange,
    handleVariationImageChange,
    handleImageUpload,
    handleImageRemove,
    handleSetMainImage,

    // Variation management
    toggleVariationExpansion,
    toggleVariationSelection,
    selectAllVariations,
    deselectAllVariations,
    removeSelectedVariations,
    regenerateVariations,

    // Bulk actions
    handleBulkShopAssignment,
    handleBulkPriceChange,
    applyBulkActions,

    // Dropshipping variation handlers
    handleVariationDropshippingChange,
    applyBulkDropshipping,
  };
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default useFormData;